// Business-local thin client for the facility-community endpoints. Mirrors the
// other `libs/*Api.ts` wrappers: it is a thin pass-through over the SHARED
// `communityApi` (`@bw/shared/api`) so the business components import from one
// module seam and the network shape is mocked in tests at `@/libs/communityApi`.
//
// Scope (S2.2 — the operator's facility community / "club"):
//   - getFacilityCommunity   GET   /communities/facility/{facilityId}  -> CommunityResponse | null
//   - createFacilityCommunity POST /communities/facility/{facilityId}  -> CommunityResponse
//   - updateCommunity        PATCH /communities/{communityId}          -> CommunityResponse
//
// Scope (S2.4 — the operator posts-as-facility + moderates the community feed):
//   - createCommunityPost    POST   /communities/{communityId}/posts   -> PostResponse
//   - deleteCommunityPost    DELETE /communities/posts/{postId}        -> void
//   - archiveCommunity       DELETE /communities/{communityId}         -> void
// RBAC stays SERVER-side (facility:community to post / remove; archive is tighter —
// the community entity-owner OR the FACILITY OWNER only, NOT a plain manager); a
// 403 reaches the caller and must not be swallowed. The post-delete targets the
// POST id (not the community id), matching the shared client's `deletePost(postId)`.
// Archive is a SOFT-archive: the backend sets the community's status to "archived"
// (the row + its data are retained, not hard-deleted) and it disappears from
// players; the route returns { status: "archived" }.
//
// The GET returns null (the route's response_model is `CommunityResponse | None`:
// a 200 + null body when the facility has no community yet — NOT a 404, NOT an
// error). RBAC is enforced SERVER-side (facility:community to create,
// community:manage to update); a denied caller gets a 403 the caller must not
// swallow. The generated CommunityResponse carries owner_facility_id (S2.1).
import { communityApi } from "@bw/shared/api";
// CommunityCreateInput is the create CONTRACT type: only `name` is required (the
// backend defaults join_mode / post_permission / visibility), reflecting wire
// reality. CommunityResponse / CommunityUpdate are the generated DTOs.
import type { CommunityCreateInput } from "@bw/shared/api";
import type {
  CommunityResponse,
  CommunityUpdate,
  PostResponse,
  CommunityAdmissionInvitationCreate,
  CommunityAdmissionListResponse,
  CommunityAdmissionResponse,
} from "@bw/shared/types/generated";

export type {
  CommunityCreateInput,
  CommunityResponse,
  CommunityUpdate,
  PostResponse,
  CommunityAdmissionInvitationCreate,
  CommunityAdmissionListResponse,
  CommunityAdmissionResponse,
};

export function getFacilityCommunity(
  facilityId: string,
): Promise<CommunityResponse | null> {
  return communityApi.getFacilityCommunity(facilityId);
}

export function createFacilityCommunity(
  facilityId: string,
  data: CommunityCreateInput,
): Promise<CommunityResponse> {
  return communityApi.createFacilityCommunity(facilityId, data);
}

export function updateCommunity(
  communityId: string,
  data: CommunityUpdate,
): Promise<CommunityResponse> {
  return communityApi.update(communityId, data);
}

// S2.4 — post an announcement into the community. The body carries `content` plus
// optional `media_urls` (permanent public urls from the media-upload flow — the
// backend's create_post accepts them); identity + community scoping are server-side.
// `content` may be empty when the post carries ≥1 photo (mirrors the player composer).
export function createCommunityPost(
  communityId: string,
  data: { content: string; media_urls?: string[] },
): Promise<PostResponse> {
  return communityApi.createPost(communityId, data);
}

// Author-edit — PATCH /communities/posts/{postId} with CONTENT ONLY. The business
// surface deliberately sends `{ content }` and NEVER media_urls: the PATCH has
// exclude_unset semantics, so an omitted media_urls leaves the post's existing
// photos UNTOUCHED (wiring the full photo-edit flow here is disproportionate —
// the player EditPostDialog owns media editing). Author-only is enforced
// server-side (403; the FE gate only shows the control); 422 when the merged
// result would be empty. The response carries the server-set `edited_at`.
export function updateCommunityPost(
  postId: string,
  data: { content: string },
): Promise<PostResponse> {
  return communityApi.updatePost(postId, { content: data.content });
}

// S2.4 — moderate: remove a post. The shared client deletes BY POST id.
export function deleteCommunityPost(postId: string): Promise<void> {
  return communityApi.deletePost(postId);
}

// Archive (soft-delete) the facility's community. DELETE /communities/{id} sets the
// community's status to "archived" (data retained, not hard-deleted) and hides it
// from players. RBAC is server-authoritative (entity-owner OR facility owner); a 403
// reaches the caller and must not be swallowed.
export function archiveCommunity(communityId: string): Promise<void> {
  return communityApi.delete(communityId);
}

export function listCommunityAdmissions(
  communityId: string,
  params?: { status?: string; limit?: number; offset?: number },
): Promise<CommunityAdmissionListResponse> {
  return communityApi.listAdmissions(communityId, params);
}

export function createCommunityInvitation(
  communityId: string,
  data: CommunityAdmissionInvitationCreate,
): Promise<CommunityAdmissionResponse> {
  return communityApi.createInvitation(communityId, data);
}

export function approveCommunityRequest(
  admissionId: string,
): Promise<CommunityAdmissionResponse> {
  return communityApi.approveRequest(admissionId);
}

export function declineCommunityRequest(
  admissionId: string,
): Promise<CommunityAdmissionResponse> {
  return communityApi.declineRequest(admissionId);
}

export function revokeCommunityInvitation(
  admissionId: string,
): Promise<CommunityAdmissionResponse> {
  return communityApi.revokeInvitation(admissionId);
}
